/**
 * Image Orientation Utility
 * Handles auto-rotation of images based on EXIF orientation data.
 *
 * Uses `createImageBitmap(file, { imageOrientation: 'from-image' })` so the
 * browser bakes EXIF rotation into the pixel data exactly once. The previous
 * `new Image()` + manual canvas rotation path double-rotated photos on
 * browsers that auto-orient `<img>` by default (Chrome 81+, Safari 13.4+,
 * Firefox 77+).
 */

/**
 * Reads EXIF orientation tag from a JPEG file's binary data.
 * @param {File} file - The image file
 * @returns {Promise<number>} Orientation value (1-8), defaults to 1
 */
export const getExifOrientation = (file) => {
  return new Promise((resolve) => {
    if (!file || file.size < 2) {
      resolve(1);
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const view = new DataView(e.target.result);

        // Check JPEG SOI marker (0xFFD8)
        if (view.byteLength < 2 || view.getUint16(0) !== 0xFFD8) {
          resolve(1);
          return;
        }

        let offset = 2;
        while (offset < view.byteLength - 2) {
          const marker = view.getUint16(offset);

          // APP1 marker (0xFFE1) contains EXIF
          if (marker === 0xFFE1) {
            // Check "Exif\0\0" header
            if (
              offset + 10 < view.byteLength &&
              view.getUint32(offset + 4) === 0x45786966 && // "Exif"
              view.getUint16(offset + 8) === 0x0000
            ) {
              const tiffStart = offset + 10;
              const bigEndian = view.getUint16(tiffStart) === 0x4D4D;

              // Verify TIFF magic number (42)
              const magic = bigEndian
                ? view.getUint16(tiffStart + 2)
                : view.getUint16(tiffStart + 2, true);
              if (magic !== 0x002A) {
                resolve(1);
                return;
              }

              const ifdOffset = bigEndian
                ? view.getUint32(tiffStart + 4)
                : view.getUint32(tiffStart + 4, true);

              const ifdStart = tiffStart + ifdOffset;
              if (ifdStart + 2 > view.byteLength) {
                resolve(1);
                return;
              }

              const entryCount = bigEndian
                ? view.getUint16(ifdStart)
                : view.getUint16(ifdStart, true);

              for (let i = 0; i < entryCount; i++) {
                const entryOffset = ifdStart + 2 + i * 12;
                if (entryOffset + 12 > view.byteLength) break;

                const tag = bigEndian
                  ? view.getUint16(entryOffset)
                  : view.getUint16(entryOffset, true);

                if (tag === 0x0112) {
                  const value = bigEndian
                    ? view.getUint16(entryOffset + 8)
                    : view.getUint16(entryOffset + 8, true);
                  resolve(value >= 1 && value <= 8 ? value : 1);
                  return;
                }
              }
            }
            resolve(1);
            return;
          }

          // Skip non-APP1 markers
          if ((marker & 0xFF00) === 0xFF00 && marker !== 0xFFFF) {
            offset += 2 + view.getUint16(offset + 2);
          } else {
            offset++;
          }
        }

        resolve(1);
      } catch {
        resolve(1);
      }
    };
    reader.onerror = () => resolve(1);
    // EXIF lives near the start; first 64KB is plenty.
    reader.readAsArrayBuffer(file.slice(0, 65536));
  });
};

/**
 * Auto-corrects image orientation based on EXIF data.
 *
 * - JPEGs with EXIF orientation 2-8 are decoded via `createImageBitmap` with
 *   `imageOrientation: 'from-image'` so the browser bakes the rotation into
 *   pixels exactly once, then re-encoded to a new File (EXIF stripped).
 * - JPEGs with orientation 1, JPEGs with no EXIF, and non-JPEGs (PNG, WebP,
 *   GIF, BMP) skip the canvas pass entirely — original file is returned
 *   unchanged, no re-encoding, no quality loss.
 *
 * @param {File} file - The image file to process
 * @returns {Promise<{file: File, dataUrl: string, wasCorrected: boolean}>}
 */
export const autoCorrectImageOrientation = async (file) => {
  const orientation = await getExifOrientation(file);

  if (orientation <= 1) {
    const dataUrl = await readFileAsDataUrl(file);
    return { file, dataUrl, wasCorrected: false };
  }

  const bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' });

  try {
    const canvas = document.createElement('canvas');
    canvas.width = bitmap.width;
    canvas.height = bitmap.height;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(bitmap, 0, 0);

    const mimeType = file.type || 'image/jpeg';
    const blob = await new Promise((resolve, reject) => {
      canvas.toBlob(
        (b) => (b ? resolve(b) : reject(new Error('Failed to convert canvas to blob'))),
        mimeType,
        0.95
      );
    });

    const correctedFile = new File([blob], file.name, {
      type: mimeType,
      lastModified: Date.now(),
    });
    const dataUrl = canvas.toDataURL(mimeType, 0.95);

    return { file: correctedFile, dataUrl, wasCorrected: true };
  } finally {
    if (bitmap && typeof bitmap.close === 'function') bitmap.close();
  }
};

function readFileAsDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => resolve(e.target.result);
    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.readAsDataURL(file);
  });
}
